import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UseVersionsResult } from '../../hooks/useVersions';
import { GameVersion } from '../../models/version';
import { IconPlay, IconArrowL, IconPackage, IconClock, IconMore } from '../Icons';
import InlineError from '../InlineError';
import EngineUpgradeBadge from './EngineUpgradeBadge';
import './VersionTree.css';

const RESTORE_HINT =
  'This version hasn’t been used in a while, so its playable build was cleared to save space. ' +
  'Restore it to build it again, then you can play, continue from, or publish it.';

interface VersionControlPanelProps {
  projectId: string;
  versions: UseVersionsResult;
  isChatRunning: boolean;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// Groups versions into a parent→children map so the panel can render the fork tree. Versions whose
// parent isn't present (or is null) are treated as roots; siblings are ordered newest-first so the
// most recent branch sits leftmost.
function buildChildrenMap(versions: GameVersion[]): Map<string | null, GameVersion[]> {
  const ids = new Set(versions.map((v) => v.id));
  const childrenByParent = new Map<string | null, GameVersion[]>();
  for (const v of versions) {
    const parentKey = v.parentVersionId && ids.has(v.parentVersionId) ? v.parentVersionId : null;
    const siblings = childrenByParent.get(parentKey) ?? [];
    siblings.push(v);
    childrenByParent.set(parentKey, siblings);
  }
  for (const siblings of childrenByParent.values()) {
    siblings.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }
  return childrenByParent;
}

export default function VersionControlPanel({ projectId, versions, isChatRunning }: VersionControlPanelProps) {
  const { versions: list, busyVersionId, error, isLoading, activate, publish, unpublish, restore } = versions;
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onPlay = (version: GameVersion) => {
    navigate(`/play/${projectId}/version/${version.id}`);
  };

  const childrenByParent = buildChildrenMap(list);
  const hasPublished = list.some((v) => v.isPublished);

  const renderCard = (version: GameVersion) => {
    const isBusy = busyVersionId === version.id;
    const isBuilding = !version.isInProgress && version.webBuildState === 'Building';
    const isPublishing = version.publishedBuildState === 'Building';
    const publicCannotPlay = version.isPublished && !isPublishing && version.publishedBuildState !== 'Ready';
    // A version with no current web build (evicted by retention, or a build that failed) must be
    // rebuilt before it can be played, continued from, or published.
    const needsRestore = version.webBuildState === 'NotBuilt' || version.webBuildState === 'Failed';
    const restoreOffered = needsRestore && !version.engineUpgradeRequired;
    const publishOffered =
      !isBuilding && !needsRestore && !isPublishing && (!version.isPublished || publicCannotPlay);
    const publicCannotPlayNote = !publicCannotPlay
      ? null
      : publishOffered
        ? t('versions.publicCannotPlayPublish')
        : restoreOffered
          ? t('versions.publicCannotPlayRestore')
          : t('versions.publicCannotPlay');
    const isMenuOpen = openMenuId === version.id;
    const description = `${version.changeSummary || 'No summary'} · ${formatDate(version.createdAt)}`;

    const runAndClose = (fn: () => void) => () => {
      setOpenMenuId(null);
      fn();
    };

    const engineNote = (
      <div key="engine" className="vt-menu-note" role="note">
        Made with an older engine version, so it can’t be restored.
      </div>
    );

    const activateItem = (
      <button
        key="activate"
        type="button"
        className="vt-menu-item"
        onClick={runAndClose(() => activate(version.id))}
        disabled={isBusy || isChatRunning}
        title="Continue making changes from this version"
      >
        <IconArrowL size={14} /> Set as active
      </button>
    );

    const menuItems: ReactNode[] = [];
    if (isBuilding) {
      menuItems.push(
        <button key="restoring" type="button" className="vt-menu-item" disabled title="This version is being built">
          <IconClock size={14} /> Restoring…
        </button>,
      );
    } else if (needsRestore && version.engineUpgradeRequired) {
      menuItems.push(engineNote);
      if (!version.isActive) menuItems.push(activateItem);
    } else if (restoreOffered) {
      menuItems.push(
        <button
          key="restore"
          type="button"
          className="vt-menu-item"
          onClick={runAndClose(() => restore(version.id))}
          disabled={isBusy}
          title={RESTORE_HINT}
        >
          <IconClock size={14} /> Restore
        </button>,
      );
    } else {
      menuItems.push(
        <button
          key="play"
          type="button"
          className="vt-menu-item"
          onClick={runAndClose(() => onPlay(version))}
          disabled={isBusy}
          title="Play this version"
        >
          <IconPlay size={14} /> Play
        </button>,
      );
      if (!version.isActive) menuItems.push(activateItem);
      if (isPublishing) {
        menuItems.push(
          <button key="publishing" type="button" className="vt-menu-item" disabled title="Building the public version of this game">
            <IconClock size={14} /> Publishing…
          </button>,
        );
      } else {
        if (publishOffered) {
          menuItems.push(
            <button
              key="publish"
              type="button"
              className="vt-menu-item"
              onClick={runAndClose(() => publish(version.id))}
              disabled={isBusy}
              title="Make this the public version shown on the Entire community page"
            >
              <IconPackage size={14} /> Publish
            </button>,
          );
        }
        if (version.isPublished) {
          menuItems.push(
            <button
              key="unpublish"
              type="button"
              className="vt-menu-item"
              onClick={runAndClose(() => unpublish())}
              disabled={isBusy}
              title="Remove this game from the Entire community page"
            >
              <IconPackage size={14} /> Unpublish
            </button>,
          );
        }
      }
    }

    return (
      <div className={`card vt-card${version.isActive ? ' active' : ''}`}>
        <div className="vt-card-head">
          <span className="mono" style={{ fontWeight: 600 }}>v{version.semver}</span>
          {version.isEngineUpgrade && <EngineUpgradeBadge engineVersion={version.engineVersion} />}
          {version.isActive && <span className="chip coral" style={{ fontSize: 11 }}>Active</span>}
          {version.isPublished && !isPublishing && (
            <span className={`chip ${publicCannotPlay ? 'warn' : 'ok'}`} style={{ fontSize: 11 }}>Published</span>
          )}
          {isPublishing && <span className="chip" style={{ fontSize: 11 }}>Publishing…</span>}
          {version.isInProgress ? (
            <span className="chip" style={{ fontSize: 11 }} title="A running chat is still making this version">
              In progress
            </span>
          ) : (
            <div className="vt-menu-wrap">
              <button
                type="button"
                className="vt-menu-btn"
                aria-label="Version options"
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                onClick={() => setOpenMenuId(isMenuOpen ? null : version.id)}
              >
                <IconMore size={16} />
              </button>
              {isMenuOpen && (
                <>
                  <div className="vt-menu-backdrop" onClick={() => setOpenMenuId(null)} />
                  <div className="vt-menu" role="menu">
                    {menuItems}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div
          className="muted"
          style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={description}
        >
          {description}
        </div>
        {publicCannotPlayNote && (
          <div role="note" style={{ fontSize: 12, color: 'var(--warn)' }}>
            {publicCannotPlayNote}
          </div>
        )}
      </div>
    );
  };

  // A subtree is a vertical column: the version's children (newer) sit above, joined by connectors
  // down to the version's card at the bottom.
  const renderSubtree = (version: GameVersion) => {
    const children = childrenByParent.get(version.id) ?? [];
    return (
      <div className="vt-node" key={version.id}>
        {children.length > 0 && (
          <>
            <div className="vt-children">
              {children.map((child) => (
                <div className="vt-child" key={child.id}>
                  {renderSubtree(child)}
                </div>
              ))}
            </div>
            <div className="vt-stem" />
          </>
        )}
        {renderCard(version)}
      </div>
    );
  };

  const roots = childrenByParent.get(null) ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, padding: '20px 28px', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <span className="eyebrow">Version control</span>
        <span className="muted-2" style={{ fontSize: 12 }}>
          Revert, continue from, publish, or play any version of your game.
        </span>
      </div>

      {!isLoading && list.length > 0 && !hasPublished && (
        <div style={{ fontSize: 12, color: 'var(--text-5)', marginBottom: 12 }}>
          Not published — this game isn't shown on the Entire community page yet. Publish a version to share it.
        </div>
      )}

      {isChatRunning && (
        <div style={{ fontSize: 12, color: 'var(--text-5)', marginBottom: 12 }}>
          A chat is running — switching versions is paused until it finishes.
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 12 }}>
          <InlineError>{error}</InlineError>
        </div>
      )}

      {isLoading ? (
        <div className="muted" style={{ marginTop: 24 }}>Loading versions…</div>
      ) : list.length === 0 ? (
        <div className="muted" style={{ marginTop: 24 }}>No versions yet — make a change in chat to create one.</div>
      ) : (
        <div className="vt-forest">
          {roots.map((root) => renderSubtree(root))}
        </div>
      )}
    </div>
  );
}
